import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { Prompt, StaticContext } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import modifiersStore from '../../store/modifiersStore';
import ModifiersOptions from '../ModifiersOptions/ModifiersOptions';
import { SCREENS } from 'src/constants/screens';
import integrationStore from 'src/mobx/integrationStore';
import { localeStore } from 'src/mobx/localesStore';
import { getTranslate, scrollToErrorElement } from 'src/utils/helpers';
import { confirmZeroPrice } from 'src/utils/modalConfirmations';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import { getPromptProps } from 'src/view/components/CustomPrompt/helper';
import LoadingOverlay from 'src/view/components/LoadingOverlay/LoadingOverlay';
import HeaderBack from 'src/view/containers/Headers/components/HeaderBack/HeaderBack';
import Headers from 'src/view/containers/Headers/Headers';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import MenuManagTabItemMainDetails from 'src/view/screens/MenuManagementScreen/containers/MenuManagTabItemMainDetails/MenuManagTabItemMainDetails';
import { BUTTON_TITLE } from '~/constants/main';

type TProps = RouteComponentProps<{ id: string }, StaticContext, undefined>;

const ModifiersEdit: React.FC<TProps> = observer((props: TProps): JSX.Element => {
  useEffect(() => {
    if (!modifiersStore.modifiersCount) modifiersStore.fetchModifiersCount();

    if (props.match.params.id) {
      modifiersStore.setModifier(modifiersStore.pagination.data.find((modifier) => modifier.id === Number(props.match.params.id)));
      if (!modifiersStore.modifier?.id) modifiersStore.getModifier(props.match.params.id);
      return;
    }

    modifiersStore.setModifier();
  }, []);

  const clickSave = () => {
    if (!modifiersStore.modifier) return;
    modifiersStore.setValidation();

    if (!modifiersStore.validation.valid) {
      scrollToErrorElement();
      return;
    }

    if (zeroPrice) {
      confirmZeroPrice({ onConfirm: save });
      return;
    }

    save();
  };

  const save = () => {
    const { modifier, createModifier, updateModifier } = modifiersStore;
    if (!modifier) return;

    if (modifier.is_draft) {
      createModifier();
    } else {
      updateModifier(modifier.id);
    }
  };

  const zeroPrice = Boolean(modifiersStore.modifier?.options.some((option) => option.price && Number(option.price) === 0));

  return (
    <>
      <Headers>
        <HeaderBack title={modifiersStore.modifier ? getTranslate(modifiersStore.modifier.name) : ''} noTranslation path={SCREENS.MODIFIERS()} />
      </Headers>
      <ScrollableBlock showScrollbar className="full-height">
        {modifiersStore.modifier ? (
          <MenuManagTabItemMainDetails
            name={{ enName: modifiersStore.modifier.name.en, arName: modifiersStore.modifier.name.ar }}
            externalId={modifiersStore.modifier.external_id}
            status={modifiersStore.modifier.status}
            validation={modifiersStore.validation.name}
            nameInputTitle="modifier_name"
            posNameRestriction={integrationStore.posRestrictions?.modifierName}
            posAvailabilityRestriction={integrationStore.posRestrictions?.modifierGlobalAvailability}
            changeName={modifiersStore.changeModifierName}
            changeExternalId={modifiersStore.changeModifierExternalId}
            changeAvailability={modifiersStore.changeModifierAvailability}
            setValidation={modifiersStore.setValidation}
          />
        ) : null}
        <ModifiersOptions />
        <Button title={localeStore.t(BUTTON_TITLE.SAVE)} onClick={clickSave} loading={modifiersStore.isLoading} className="mt-8" />
      </ScrollableBlock>
      <LoadingOverlay visible={modifiersStore.isLoading} />
      <Prompt when={modifiersStore.edited} message={getPromptProps()} />
    </>
  );
});

export default ModifiersEdit;
